import type { useAppConfig } from "@core/composables/data/useAppConfig";
import type { ViewType } from "@core/composables/state/useNavigationRouter";

type AppConfig = ReturnType<typeof useAppConfig>["config"];
type PermissionKey = keyof AppConfig["permissions"];

type AdminAccessRule =
  { kind: "permission"; permission: PermissionKey } | { kind: "youparkCustomer" } | { kind: "employeeOnly" };

const adminViewRules = [
  { view: "remit", rule: { kind: "permission", permission: "remits" } },
  { view: "remit-tab", rule: { kind: "permission", permission: "remits" } },
  { view: "remit-allocations", rule: { kind: "permission", permission: "remits" } },
  { view: "remit-creditors", rule: { kind: "permission", permission: "remits" } },
  { view: "statistics", rule: { kind: "permission", permission: "statistics" } },
  { view: "telemetry", rule: { kind: "permission", permission: "telemetry" } },
  { view: "telemetry-annotations", rule: { kind: "permission", permission: "telemetry" } },
  { view: "applications", rule: { kind: "permission", permission: "applications" } },
  { view: "users", rule: { kind: "permission", permission: "users" } },
  { view: "user-create", rule: { kind: "permission", permission: "users" } },
  { view: "user-edit", rule: { kind: "permission", permission: "users" } },
  { view: "organizations", rule: { kind: "permission", permission: "organizations" } },
  { view: "organizations-create", rule: { kind: "permission", permission: "organizations" } },
  { view: "organizations-details", rule: { kind: "permission", permission: "organizations" } },
  { view: "organizations-details-applications", rule: { kind: "permission", permission: "organizations" } },
  { view: "organizations-details-users", rule: { kind: "permission", permission: "organizations" } },
  { view: "organizations-details-subs", rule: { kind: "permission", permission: "organizations" } },
  { view: "audits", rule: { kind: "permission", permission: "audits" } },
  { view: "vehicle-registry", rule: { kind: "permission", permission: "vehicleRegistry" } },
  { view: "manual-approval", rule: { kind: "permission", permission: "manualApproval" } },
  { view: "traffic-control", rule: { kind: "permission", permission: "trafficControl" } },
  { view: "system", rule: { kind: "permission", permission: "system" } },
  { view: "cameras-overview", rule: { kind: "permission", permission: "camerasOverview" } }
] as const satisfies readonly { view: ViewType; rule: AdminAccessRule }[];

const adminRulesByView = new Map<ViewType, AdminAccessRule>(adminViewRules.map(def => [def.view, def.rule] as const));

export const adminViews = new Set<ViewType>(adminViewRules.map(def => def.view));

export const canAccessYouparkSection = (config: AppConfig, organizationId?: string) => {
  if (config.isEmployee || config.canAccessYouParkCustomer) {
    return true;
  }
  if (organizationId && config.lots.some(lot => lot.organizationId === organizationId)) {
    return true;
  }
  return false;
};

export const canAccessAdminView = (config: AppConfig, view: string) => {
  const rule = adminRulesByView.get(view as ViewType);
  if (!rule) {
    return true;
  }

  if (config.isEmployee) {
    return true;
  }

  if (rule.kind === "permission") {
    return config.permissions[rule.permission] === true;
  }

  if (rule.kind === "youparkCustomer") {
    return config.canAccessYouParkCustomer === true;
  }

  return false;
};

export const youparkViews = new Set<ViewType>([
  "youpark",
  "youpark-customer-register",
  "youpark-subscriptions",
  "youpark-invoices",
  "youpark-transactions",
  "youpark-credit",
  "youpark-booking-reservations",
  "youpark-free-visits",
  "youpark-reports",
  "youpark-insights",
  "youpark-business-insights",
  "youpark-todos",
  "youpark-access",
  "youpark-customer-admin",
  "youpark-customer-admin-locations",
  "youpark-customer-admin-locations-autopass",
  "youpark-customer-admin-payment-kiosks",
  "youpark-customer-admin-invoicing",
  "youpark-customer-admin-areas",
  "youpark-customer-admin-access",
  "youpark-customer-admin-free-visit-kiosks",
  "youpark-customer-admin-fees"
]);

export const youparkCustomerAdminViews = new Set<ViewType>([
  "youpark-customer-admin",
  "youpark-customer-admin-locations",
  "youpark-customer-admin-locations-autopass",
  "youpark-customer-admin-payment-kiosks",
  "youpark-customer-admin-invoicing",
  "youpark-customer-admin-areas",
  "youpark-customer-admin-access",
  "youpark-customer-admin-free-visit-kiosks",
  "youpark-customer-admin-fees"
]);

export const canAccessYouparkCustomerAdminSection = (config: AppConfig) => config.permissions.youparkSettings === true;

export const canAccessRestrictedView = (config: AppConfig, view: string, organizationId?: string) => {
  const canAccessYouparkOrganization = canAccessYouparkSection(config, organizationId);

  if (view.startsWith("youpark-subscriptions")) {
    return canAccessYouparkOrganization && config.permissions.youparkSubscriptions === true;
  }
  if (view.startsWith("youpark-todos")) {
    return canAccessYouparkOrganization && config.permissions.youparkTodos === true;
  }
  if (view.startsWith("youpark-customer-register")) {
    return canAccessYouparkOrganization && config.permissions.youparkCustomerSubscriptionsRead === true;
  }
  if (view.startsWith("youpark-invoice")) {
    return canAccessYouparkOrganization && config.permissions.youparkInvoice === true;
  }
  if (view.startsWith("youpark-transactions")) {
    return canAccessYouparkOrganization && config.permissions.youpayAccounting === true;
  }
  if (view.startsWith("youpark-credit")) {
    if (!canAccessYouparkOrganization) {
      return false;
    }
    if (config.isYouParkCustomer) {
      return true;
    }
    return (
      config.permissions.youparkInvoiceWrite === true ||
      config.permissions.youpayAccounting === true ||
      config.permissions.youpayRefund === true
    );
  }
  if (view.startsWith("youpark-free-visits")) {
    return canAccessYouparkOrganization && config.permissions.youparkFreeVisitRead === true;
  }
  if (view.startsWith("youpark-reports")) {
    return canAccessYouparkOrganization && config.permissions.youparkAccounting === true;
  }
  if (view.startsWith("youpark-access")) {
    return canAccessYouparkOrganization && config.permissions.access === true && config.isYouParkCustomer !== true;
  }
  if (view.startsWith("youpark-customer-admin")) {
    return canAccessYouparkOrganization && config.permissions.youparkSettings === true;
  }
  if (adminRulesByView.has(view as ViewType)) {
    return canAccessAdminView(config, view);
  }
  if (youparkCustomerAdminViews.has(view as ViewType)) {
    return canAccessYouparkOrganization && canAccessYouparkCustomerAdminSection(config);
  }
  if (youparkViews.has(view as ViewType)) {
    return canAccessYouparkOrganization;
  }
  return true;
};

export type YouparkMenuContext = {
  allowBusinessInsights: boolean;
  hasParkingLots: boolean;
};

export const canAccessYouparkMenuView = (
  config: AppConfig,
  view: string,
  organizationId: string | undefined,
  context: YouparkMenuContext
) => {
  if (!canAccessRestrictedView(config, view, organizationId)) {
    return false;
  }
  if (view.startsWith("youpark-business-insights")) {
    return context.allowBusinessInsights;
  }
  if (view.startsWith("youpark-booking-reservations")) {
    return context.hasParkingLots;
  }
  return true;
};

export const hasAdministrationAccess = (config: AppConfig) => {
  for (const view of adminViews) {
    if (canAccessAdminView(config, view)) {
      return true;
    }
  }
  return false;
};
